<template>
  <ContentWrap>
    <el-alert
      class="mb-15px"
      type="info"
      :closable="false"
      title="OSS 容量套餐（买断）：与「配置管理」中的默认赠送分开。此处只维护可售档位与价格；租户在「OSS容量扩容」页单独加购。"
    />
  </ContentWrap>

  <ContentWrap>
    <div class="mb-15px">
      <el-button type="primary" @click="openForm()" v-hasPermi="['saasbilling:oss-capacity:pricing']">
        新增套餐
      </el-button>
    </div>
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column label="编码" prop="moduleCode" min-width="120" />
      <el-table-column label="名称" prop="planName" min-width="140" />
      <el-table-column label="容量" min-width="100">
        <template #default="{ row }">{{ formatBytes(row.capacityBytes) }}</template>
      </el-table-column>
      <el-table-column label="买断价" width="120" align="right">
        <template #default="{ row }">¥{{ ((row.price || 0) / 100).toFixed(2) }}</template>
      </el-table-column>
      <el-table-column label="状态" width="90" align="center">
        <template #default="{ row }">
          <el-tag :type="row.status === 1 ? 'success' : 'info'" size="small">
            {{ row.status === 1 ? '上架' : '下架' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="100" align="center" fixed="right">
        <template #default="{ row }">
          <el-button
            link
            type="primary"
            @click="openForm(row)"
            v-hasPermi="['saasbilling:oss-capacity:pricing']"
          >
            编辑
          </el-button>
        </template>
      </el-table-column>
    </el-table>
  </ContentWrap>

  <Dialog v-model="dialogVisible" :title="formData.id ? '编辑容量套餐' : '新增容量套餐'" width="480px">
    <el-form ref="formRef" :model="formData" :rules="rules" label-width="100px">
      <el-form-item label="套餐编码" prop="moduleCode">
        <el-input v-model="formData.moduleCode" placeholder="如 oss_10gb" :disabled="!!formData.id" />
      </el-form-item>
      <el-form-item label="套餐名称" prop="planName">
        <el-input v-model="formData.planName" placeholder="如 10GB 容量包" />
      </el-form-item>
      <el-form-item label="容量(GB)" prop="capacityGb">
        <el-input-number v-model="formData.capacityGb" :min="0.01" :precision="2" :step="1" />
      </el-form-item>
      <el-form-item label="买断价(元)" prop="priceYuan">
        <el-input-number v-model="formData.priceYuan" :min="0.01" :precision="2" :step="1" />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-radio-group v-model="formData.status">
          <el-radio :value="1">上架</el-radio>
          <el-radio :value="0">下架</el-radio>
        </el-radio-group>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="submit">保存</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import * as OssApi from '@/api/system/ossCapacity'
import { formatBytes, gbToBytes } from '@/api/system/ossCapacity'

defineOptions({ name: 'SystemOssCapacityPricing' })

const message = useMessage()
const loading = ref(false)
const saving = ref(false)
const list = ref<OssApi.OssCapacityPackageVO[]>([])
const dialogVisible = ref(false)
const formRef = ref()
const formData = ref({
  id: undefined as number | undefined,
  moduleCode: '',
  planName: '',
  capacityGb: 10,
  priceYuan: 99,
  status: 1
})

const rules = {
  moduleCode: [{ required: true, message: '编码不能为空', trigger: 'blur' }],
  planName: [{ required: true, message: '名称不能为空', trigger: 'blur' }],
  capacityGb: [{ required: true, message: '容量不能为空', trigger: 'blur' }],
  priceYuan: [{ required: true, message: '价格不能为空', trigger: 'blur' }],
  status: [{ required: true, message: '状态不能为空', trigger: 'change' }]
}

const loadList = async () => {
  loading.value = true
  try {
    list.value = (await OssApi.getOssCapacityPricingList()) || []
  } finally {
    loading.value = false
  }
}

const openForm = (row?: OssApi.OssCapacityPackageVO) => {
  if (row) {
    formData.value = {
      id: row.id,
      moduleCode: row.moduleCode,
      planName: row.planName,
      capacityGb: Number(((row.capacityBytes || 0) / (1024 * 1024 * 1024)).toFixed(2)),
      priceYuan: Number(((row.price || 0) / 100).toFixed(2)),
      status: row.status
    }
  } else {
    formData.value = {
      id: undefined,
      moduleCode: '',
      planName: '',
      capacityGb: 10,
      priceYuan: 99,
      status: 1
    }
  }
  dialogVisible.value = true
}

const submit = async () => {
  await formRef.value?.validate()
  saving.value = true
  try {
    await OssApi.saveOssCapacityPricing({
      id: formData.value.id,
      moduleCode: formData.value.moduleCode,
      planName: formData.value.planName,
      capacityBytes: gbToBytes(formData.value.capacityGb),
      price: Math.round(formData.value.priceYuan * 100),
      status: formData.value.status
    })
    message.success('保存成功')
    dialogVisible.value = false
    await loadList()
  } finally {
    saving.value = false
  }
}

onMounted(loadList)
</script>
